/**
 * ServerRelayProvider
 *
 * Mirrors every client event to POST /api/analytics/events so the backend
 * can fan out to any vendor (PostHog, data warehouse) without a
 * frontend deploy. This is the migration escape hatch: once we want to cut
 * over to server-only delivery, we remove the other providers and keep only
 * this one.
 */
import type { AnalyticsProvider, AnalyticsContext } from '../analytics';
import { API_BASE_URL } from '../../../api/config';
import { ANALYTICS_SESSION_KEY, ANALYTICS_ANONYMOUS_ID_KEY } from '../analytics';
import { createFileLogger } from '../../../utils/logger';

const log = createFileLogger('SERVER_RELAY');

interface RelayEvent {
  event: string;
  props: Record<string, unknown>;
  ts: string;
  correlation_id?: string;
}

export class ServerRelayProvider implements AnalyticsProvider {
  readonly name = 'server_relay';
  private queue: RelayEvent[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private userId?: string;
  private orgId?: string;
  private readonly batchMs = 2000;
  private readonly maxBatch = 50;

  async init(_ctx: AnalyticsContext): Promise<void> {
    if (_ctx.userId) this.userId = _ctx.userId;
    if (_ctx.orgId) this.orgId = _ctx.orgId;
  }

  track(event: string, props: Record<string, unknown>): void {
    this.queue.push({
      event,
      props: {
        ...props,
        ...(this.userId && { relay_user_id: this.userId }),
        ...(this.orgId && { relay_org_id: this.orgId }),
      },
      ts: new Date().toISOString(),
      correlation_id: props.correlation_id as string | undefined,
    });

    if (this.queue.length >= this.maxBatch) {
      this.sendNow();
    } else {
      this.scheduleFlush();
    }
  }

  identify(userId: string, _traits?: Record<string, unknown>): void {
    this.userId = userId;
  }

  page(_path: string, _props?: Record<string, unknown>): void {
    // Page views are tracked directly by the vendor providers; relay skips them
    // to avoid double-counting on the backend.
  }

  group(orgId: string, _traits?: Record<string, unknown>): void {
    this.orgId = orgId;
  }

  reset(): void {
    this.userId = undefined;
    this.orgId = undefined;
    this.queue = [];
  }

  async flush(): Promise<void> {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    await this.sendNow();
  }

  private scheduleFlush(): void {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.sendNow();
    }, this.batchMs);
  }

  private async sendNow(): Promise<void> {
    if (this.queue.length === 0) return;

    const batch = this.queue.splice(0, this.maxBatch);
    // Prefer authToken (Auth0 access token, aud = API audience) over id_token
    // (aud = client ID) — same convention as fetchService.ts. The backend's
    // audience allow-list only validates id_token defensively, so leading
    // with it here was causing every relay batch to 401 in production.
    const authToken = typeof localStorage !== 'undefined'
      ? (localStorage.getItem('authToken') || localStorage.getItem('id_token'))
      : null;

    // Always read from sessionStorage — canonical source of truth for session ID.
    const sessionId = typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(ANALYTICS_SESSION_KEY) || undefined
      : undefined;

    // Include anonymous_id so the server can stitch pre-auth events even when
    // the request is unauthenticated (or when the ingest endpoint receives it).
    const anonymousId = typeof localStorage !== 'undefined'
      ? localStorage.getItem(ANALYTICS_ANONYMOUS_ID_KEY) || undefined
      : undefined;

    try {
      const res = await fetch(`${API_BASE_URL}/analytics/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
          ...(sessionId && { 'X-Session-ID': sessionId }),
          ...(anonymousId && { 'X-Anonymous-ID': anonymousId }),
        },
        body: JSON.stringify({ events: batch }),
        keepalive: true,
      });
      if (!res.ok) {
        log.warn(`Server relay batch failed (HTTP ${res.status}); events dropped`);
      }
    } catch (err) {
      log.warn('Server relay batch failed; events dropped:', err);
    }
  }
}
